import { Clock, Mail, MapPin, Phone } from 'lucide-react';

import { ContactForm } from '@/components/public/ContactForm';
import { QuoteForm } from '@/components/public/QuoteForm';
import { Reveal } from '@/components/public/Reveal';
import { Seo } from '@/components/public/Seo';
import { Eyebrow, Lede, Section } from '@/components/public/Section';
import { CONTACT, NAP } from '@/lib/public-content';

/*
 * `/contact` — ROADMAP.md §8.5, and the quote form's home (§8.6).
 *
 * The live site puts the quote form on this page rather than on its own, which
 * is why `quote-form.md` was captured from `/contact`. Kept that way: a "Get a
 * Free Quote" button in the header that lands somewhere other than the form
 * would be a rebuild reorganising the site.
 */

const ICONS = { PHONE: Phone, EMAIL: Mail, LOCATION: MapPin, HOURS: Clock } as const;

export function ContactPage() {
  return (
    <>
      <Seo title={CONTACT.title} description={CONTACT.description} path="/contact" />

      {/*
        **Centred.** This hero has no photograph beside it, so left-flush left a
        wide dead margin on the right at desktop width — the thing the owner
        flagged. A full-width band with nothing to balance against wants its
        content in the middle.
      */}
      <section aria-labelledby="contact-heading" className="relative overflow-hidden bg-ink-50">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-gradient-to-b from-honey-100/70 to-ink-50"
        />

        <div className="relative mx-auto max-w-3xl px-4 py-16 text-center sm:px-6 sm:py-24">
          <Reveal>
            <Eyebrow>{CONTACT.eyebrow}</Eyebrow>
            <h1
              id="contact-heading"
              className="mt-3 text-balance text-[38px] font-bold leading-[1.08] tracking-tight sm:text-[48px]"
            >
              {CONTACT.heading}
            </h1>
            <p className="mt-4 text-[20px] font-semibold text-bark-700">{CONTACT.subheading}</p>
            <Lede className="mx-auto">{CONTACT.body}</Lede>
          </Reveal>
        </div>
      </section>

      <Section labelledBy="contact-details">
        <div className="grid gap-12 lg:grid-cols-[minmax(0,320px)_minmax(0,1fr)]">
          <Reveal>
            <h2 id="contact-details" className="text-h2">
              Reach us
            </h2>

            <ul className="mt-6 space-y-6">
              {CONTACT.details
                .filter((row) => row.label !== '')
                .map((row) => {
                  const Icon = ICONS[row.label];
                  const href =
                    row.label === 'PHONE'
                      ? NAP.phoneHref
                      : row.label === 'EMAIL'
                        ? `mailto:${NAP.email}`
                        : null;

                  return (
                    <li key={row.label} className="flex gap-3">
                      <span className="mt-0.5 inline-flex size-9 shrink-0 items-center justify-center rounded-full bg-honey-100 text-bark-700">
                        {Icon ? <Icon className="size-[18px]" aria-hidden /> : null}
                      </span>
                      <div className="min-w-0">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-500">
                          {row.label}
                        </p>
                        {href === null ? (
                          <p className="text-body text-ink-900">{row.value}</p>
                        ) : (
                          <a
                            href={href}
                            className="inline-flex min-h-touch items-center break-all text-body text-bark-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-honey-700"
                          >
                            {row.value}
                          </a>
                        )}
                        {/*
                          The site prints "Email or call for current openings"
                          under Hours as its own line. Ported as written.
                        */}
                        {row.label === 'HOURS' ? (
                          <p className="text-body text-ink-700">
                            {CONTACT.details[CONTACT.details.length - 1]?.value}
                          </p>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
            </ul>
          </Reveal>

          {/*
            `#quote` is the anchor the header's "Get a Free Quote" button and
            both home-page CTAs point at, so every one of them lands on the form
            rather than at the top of a page the visitor then has to scroll.
          */}
          <Reveal delay={80} className="scroll-mt-24 space-y-8">
            <div id="quote" className="scroll-mt-24">
              <QuoteForm />
            </div>
            {/*
              The short form second, and smaller. The quote form is what the
              business runs on; this is the quicker path for somebody who just
              has a question, and it is only worth having if that is obvious at
              a glance.
            */}
            <ContactForm />
          </Reveal>
        </div>
      </Section>
    </>
  );
}
